import React, { useCallback, useEffect, useState } from 'react';
import api from '../api';
import Modal from '../components/Modal';
import { useHinweis, hinweis } from '../components/Hinweise';
import ZeitraumWahl, { aktuellesQuartal, heuteIso, fmtDatum, fmtZahl } from '../components/ZeitraumWahl';

// Leistungsgruppen: keine Kinderliste, nur Gruppenanwesenheit je Training.
// Grundlage für die Trainerstunden und die Bahnenkosten.
const WOCHENTAGE = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const dbWochentag = (iso) => (new Date(`${String(iso).slice(0, 10)}T12:00:00Z`).getUTCDay() + 6) % 7;

// Trainingszeiten der Gruppe. Bewusst hier und nicht nur in den Ausbildungsgruppen:
// Leistungsgruppen tauchen dort nicht auf, der Weg dorthin fehlte sonst.
function Trainingszeiten({ gruppe, onGeaendert }) {
  const [wochentag, setWochentag] = useState('3');
  const [von, setVon] = useState('16:00');
  const [bis, setBis] = useState('17:00');
  const [arbeitet, setArbeitet] = useState(false);

  const termine = gruppe.termine || [];
  // Aus der Bahnbelegung ableitbare Zeiten, die noch nicht hinterlegt sind
  const ausBahnen = [...new Map((gruppe.bahnen || [])
    .map(b => [`${b.wochentag}|${b.von.slice(0, 5)}|${b.bis.slice(0, 5)}`,
      { wochentag: b.wochentag, von: b.von.slice(0, 5), bis: b.bis.slice(0, 5) }])).values()]
    .filter(b => !termine.some(t => t.wochentag === b.wochentag && t.uhrzeit_von?.slice(0, 5) === b.von));

  const anlegen = async (eintrag) => {
    setArbeitet(true);
    try {
      await api.post(`/api/admin/gruppen/${gruppe.id}/termine`, {
        wochentag: Number(eintrag.wochentag), uhrzeit_von: eintrag.von, uhrzeit_bis: eintrag.bis,
      });
      onGeaendert();
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Trainingszeit konnte nicht gespeichert werden'); }
    finally { setArbeitet(false); }
  };

  const uebernehmen = async () => {
    setArbeitet(true);
    let gesetzt = 0;
    for (const b of ausBahnen) {
      try {
        await api.post(`/api/admin/gruppen/${gruppe.id}/termine`, {
          wochentag: b.wochentag, uhrzeit_von: b.von, uhrzeit_bis: b.bis,
        });
        gesetzt++;
      } catch { /* z.B. schon vorhanden – überspringen */ }
    }
    setArbeitet(false);
    hinweis.erfolg(`${gesetzt} Trainingszeit${gesetzt === 1 ? '' : 'en'} aus der Bahnbelegung übernommen`);
    onGeaendert();
  };

  const loeschen = async (t) => {
    if (!window.confirm(`Trainingszeit ${WOCHENTAGE[t.wochentag]} ${t.uhrzeit_von?.slice(0, 5)} löschen?`)) return;
    try {
      await api.delete(`/api/admin/gruppen/${gruppe.id}/termine/${t.id}`);
      onGeaendert();
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Löschen fehlgeschlagen'); }
  };

  return (
    <div style={{ marginTop: '0.875rem', borderTop: '1px solid var(--border, #e6eaf0)', paddingTop: '0.75rem' }}>
      <div style={{ fontWeight: 700, fontSize: '0.88rem', marginBottom: '0.35rem' }}>Trainingszeiten</div>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginBottom: '0.5rem' }}>
        Daraus ergeben sich die <strong>Trainerstunden</strong>. Die Bahnbelegung darüber ist etwas anderes –
        sie bestimmt die <strong>Bahnenkosten</strong>. Beides wird getrennt gepflegt.
      </div>

      {termine.length === 0 && ausBahnen.length > 0 && (
        <div style={{
          background: '#e3f2fd', border: '1px solid #90caf9', borderRadius: 8,
          padding: '0.6rem 0.8rem', fontSize: '0.84rem', marginBottom: '0.6rem',
        }}>
          Für diese Gruppe sind {ausBahnen.length} Bahnbelegungen hinterlegt. Sollen daraus die Trainingszeiten werden?
          <div style={{ color: 'var(--text-muted)', margin: '2px 0 6px' }}>
            {ausBahnen.map(b => `${WOCHENTAGE[b.wochentag]} ${b.von}–${b.bis}`).join(' · ')}
          </div>
          <button className="btn btn-primary btn-sm" onClick={uebernehmen} disabled={arbeitet}>
            Zeiten übernehmen
          </button>
        </div>
      )}

      {termine.map(t => (
        <div key={t.id} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.3rem 0', fontSize: '0.88rem' }}>
          <span style={{ fontWeight: 600, minWidth: 96 }}>{WOCHENTAGE[t.wochentag]}</span>
          <span>{t.uhrzeit_von?.slice(0, 5)}{t.uhrzeit_bis ? ` – ${t.uhrzeit_bis.slice(0, 5)}` : ''} Uhr</span>
          <button className="btn btn-secondary btn-sm" style={{ marginLeft: 'auto', padding: '1px 8px' }}
            onClick={() => loeschen(t)}>✕</button>
        </div>
      ))}

      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.6rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>Wochentag</div>
          <select className="form-control" style={{ minWidth: 130 }} value={wochentag} onChange={e => setWochentag(e.target.value)}>
            {WOCHENTAGE.map((w, i) => <option key={w} value={i}>{w}</option>)}
          </select>
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>Von</div>
          <input type="time" className="form-control" value={von} onChange={e => setVon(e.target.value)} />
        </div>
        <div>
          <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)', marginBottom: 2 }}>Bis</div>
          <input type="time" className="form-control" value={bis} onChange={e => setBis(e.target.value)} />
        </div>
        <button className="btn btn-secondary btn-sm" disabled={arbeitet}
          onClick={() => anlegen({ wochentag, von, bis })}>+ Trainingszeit</button>
      </div>
    </div>
  );
}

function GruppeDialog({ gruppe, trainer, onClose, onGespeichert }) {
  const [form, setForm] = useState(() => ({
    name: gruppe?.name || '', trainer_id: gruppe?.trainer_id || '', ort: gruppe?.ort || '',
    aktiv: gruppe ? gruppe.aktiv : true, bemerkung: gruppe?.bemerkung || '',
  }));
  const [fehler, setFehler] = useState('');
  const set = (k, v) => { setForm(p => ({ ...p, [k]: v })); setFehler(''); };

  const absenden = async () => {
    if (!form.name.trim()) return setFehler('Bitte einen Namen angeben');
    try {
      const daten = { ...form, trainer_id: form.trainer_id || null };
      if (gruppe) await api.put(`/api/admin/leistungsgruppen/${gruppe.id}`, daten);
      else await api.post('/api/admin/leistungsgruppen', daten);
      onGespeichert();
    } catch (err) { setFehler(err.response?.data?.error || 'Speichern fehlgeschlagen'); }
  };

  return (
    <Modal title={gruppe ? gruppe.name : 'Leistungsgruppe anlegen'} onClose={onClose} size="md">
      <div className="form-group"><label className="form-label">Name *</label>
        <input className="form-control" value={form.name} maxLength={200} autoFocus onChange={e => set('name', e.target.value)} /></div>
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
        <div className="form-group"><label className="form-label">Zuständiger Trainer</label>
          <select className="form-control" value={form.trainer_id} onChange={e => set('trainer_id', e.target.value)}>
            <option value="">– keiner –</option>
            {trainer.map(t => <option key={t.id} value={t.id}>{t.vorname} {t.nachname}</option>)}
          </select></div>
        <div className="form-group"><label className="form-label">Ort</label>
          <input className="form-control" value={form.ort} maxLength={200} onChange={e => set('ort', e.target.value)} /></div>
      </div>
      <div className="form-group"><label className="form-label">Bemerkung</label>
        <textarea className="form-control" rows={2} value={form.bemerkung} onChange={e => set('bemerkung', e.target.value)} /></div>
      <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: '0.9rem', marginBottom: '0.75rem', cursor: 'pointer' }}>
        <input type="checkbox" checked={form.aktiv} onChange={e => set('aktiv', e.target.checked)} /> aktiv
      </label>
      <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', background: '#f4f7fb', borderRadius: 6, padding: '0.5rem 0.7rem' }}>
        Die Trainingszeiten stehen auf der Karte dieser Gruppe, die Bahnbelegung unter „Bahnenstunden".
        Beides zusammen ergibt Trainerstunden und Bahnenkosten.
      </div>
      {fehler && <div className="alert alert-error">{fehler}</div>}
      <div className="modal-actions">
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={absenden}>Speichern</button>
      </div>
    </Modal>
  );
}

function TrainingDialog({ gruppe, team, vorhanden, onClose, onGespeichert }) {
  const hinweis = useHinweis();
  const [datum, setDatum] = useState(vorhanden?.datum || heuteIso());
  const [anzahl, setAnzahl] = useState(vorhanden?.teilnehmer_anzahl ?? '');
  const [gewaehlt, setGewaehlt] = useState(() => new Set((vorhanden?.team || []).map(t => t.mitglied_id)));
  const [fehler, setFehler] = useState('');
  const [speichert, setSpeichert] = useState(false);

  const termin = (gruppe.termine || []).find(t => t.wochentag === dbWochentag(datum));
  const umschalten = (id) => setGewaehlt(s => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });

  const absenden = async () => {
    setSpeichert(true); setFehler('');
    try {
      const r = await api.post(`/api/admin/leistungsgruppen/${gruppe.id}/trainings`, {
        datum, teilnehmer_anzahl: anzahl, mitglieder: [...gewaehlt],
      });
      hinweis.erfolg(r.data.hinweis
        ? `Training gespeichert. ${r.data.hinweis}`
        : `Training am ${fmtDatum(datum)} gespeichert – ${r.data.team_gebucht} Personen je ${fmtZahl(r.data.stunden_je_person)} Std.`);
      onGespeichert();
    } catch (err) { setFehler(err.response?.data?.error || 'Speichern fehlgeschlagen'); }
    finally { setSpeichert(false); }
  };

  return (
    <Modal title={`${gruppe.name}: Training erfassen`} onClose={onClose} size="md">
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 0.75rem' }}>
        <div className="form-group"><label className="form-label">Datum</label>
          <input type="date" className="form-control" value={datum} max={heuteIso()} onChange={e => setDatum(e.target.value)} />
          <div style={{ fontSize: '0.78rem', marginTop: 2, color: termin ? '#2e7d32' : '#e65100' }}>
            {termin
              ? `✓ ${WOCHENTAGE[termin.wochentag]} ${termin.uhrzeit_von?.slice(0, 5)}–${termin.uhrzeit_bis?.slice(0, 5)} Uhr`
              : `⚠ ${WOCHENTAGE[dbWochentag(datum)]} ist kein Trainingstag – es lassen sich keine Stunden buchen`}
          </div>
        </div>
        <div className="form-group"><label className="form-label">Anzahl Schwimmer</label>
          <input type="number" min={0} max={200} className="form-control" value={anzahl}
            placeholder="z.B. 12" onChange={e => setAnzahl(e.target.value)} />
          <div style={{ fontSize: '0.78rem', color: 'var(--text-muted)', marginTop: 2 }}>Nur die Anzahl, keine Namen.</div>
        </div>
      </div>

      <div className="form-group">
        <label className="form-label">Wer hat betreut?</label>
        {team.length === 0 ? (
          <div style={{ fontSize: '0.85rem', color: '#e65100' }}>
            Noch keine Personen im Team – bitte zuerst unter „Team" anlegen.
          </div>
        ) : (
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.35rem' }}>
            {team.map(m => (
              <label key={m.id} style={{
                display: 'flex', alignItems: 'center', gap: 5, padding: '4px 10px', borderRadius: 16,
                cursor: termin ? 'pointer' : 'not-allowed', fontSize: '0.85rem', opacity: termin ? 1 : 0.5,
                border: `1px solid ${gewaehlt.has(m.id) ? 'var(--primary)' : 'var(--border)'}`,
                background: gewaehlt.has(m.id) ? '#e3f2fd' : '#fff',
              }}>
                <input type="checkbox" checked={gewaehlt.has(m.id)} disabled={!termin} onChange={() => umschalten(m.id)} />
                {m.nachname}, {m.vorname} <span style={{ color: 'var(--text-muted)' }}>({m.rolle_name})</span>
              </label>
            ))}
          </div>
        )}
      </div>

      {fehler && <div className="alert alert-error">{fehler}</div>}
      <div className="modal-actions">
        <button className="btn btn-secondary btn-sm" onClick={onClose}>Abbrechen</button>
        <button className="btn btn-primary btn-sm" style={{ marginLeft: 'auto' }} onClick={absenden} disabled={speichert}>
          {speichert ? 'Speichern…' : 'Training speichern'}
        </button>
      </div>
    </Modal>
  );
}

export default function Leistungsgruppen() {
  const hinweis = useHinweis();
  const [daten, setDaten] = useState(null);
  const [team, setTeam] = useState([]);
  const [trainer, setTrainer] = useState([]);
  const [zeitraum, setZeitraum] = useState(aktuellesQuartal);
  const [auswertung, setAuswertung] = useState(null);
  const [offen, setOffen] = useState(null);        // Gruppe, deren Trainings angezeigt werden
  const [trainings, setTrainings] = useState(null);
  const [dialog, setDialog] = useState(null);      // null | 'neu' | gruppe
  const [trainingDialog, setTrainingDialog] = useState(null);
  const [laden, setLaden] = useState(true);
  const [fehler, setFehler] = useState('');

  const params = zeitraum.quartal ? { jahr: zeitraum.jahr, quartal: zeitraum.quartal } : { von: zeitraum.von, bis: zeitraum.bis };

  const load = useCallback(async () => {
    try {
      const [l, a, t, b] = await Promise.all([
        api.get('/api/admin/leistungsgruppen'),
        api.get('/api/admin/leistungsgruppen/auswertung', { params }),
        api.get('/api/admin/team'),
        api.get('/api/admin/trainer-benutzer'),
      ]);
      setDaten(l.data); setAuswertung(a.data);
      setTeam(t.data.filter(m => m.aktiv)); setTrainer(b.data);
    } catch (err) { setFehler(err.response?.data?.error || 'Daten konnten nicht geladen werden'); }
    finally { setLaden(false); }
  }, [JSON.stringify(zeitraum)]); // eslint-disable-line
  useEffect(() => { load(); }, [load]);

  const ladeTrainings = useCallback(async (gruppeId) => {
    setTrainings(null);
    try {
      const r = await api.get(`/api/admin/leistungsgruppen/${gruppeId}/trainings`, { params });
      setTrainings(r.data);
    } catch { setTrainings({ trainings: [] }); }
  }, [JSON.stringify(zeitraum)]); // eslint-disable-line

  useEffect(() => { if (offen) ladeTrainings(offen.id); }, [offen, ladeTrainings]);

  const loeschen = async (t) => {
    if (!window.confirm(`Training vom ${fmtDatum(t.datum)} löschen?\n\nDie dafür gebuchten Stunden werden mit entfernt.`)) return;
    try {
      await api.delete(`/api/admin/leistungsgruppen/trainings/${t.id}`);
      hinweis.erfolg('Training gelöscht');
      ladeTrainings(offen.id); load();
    } catch (err) { hinweis.fehler(err.response?.data?.error || 'Löschen fehlgeschlagen'); }
  };

  const gruppen = daten?.gruppen || [];
  const zahl = (id) => (auswertung?.gruppen || []).find(g => g.id === id) || { trainings: 0, stunden: 0, schnitt_teilnehmer: null, personen: 0 };

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Leistungsgruppen</h2>
        <button className="btn btn-primary btn-sm" onClick={() => setDialog('neu')}>+ Leistungsgruppe</button>
      </div>
      <p style={{ color: 'var(--text-muted)', fontSize: '0.875rem', marginBottom: '1rem' }}>
        Hier wird niemand namentlich geführt. Je Training wird nur erfasst, <strong>dass</strong> es stattgefunden hat
        und <strong>mit wie vielen</strong> Schwimmern – als Grundlage für die Vergütung der betreuenden Trainer und
        für die Bahnenkosten. Die Stunden erscheinen zusammen mit allen anderen unter „Trainer-/Helferstunden".
      </p>

      {fehler && <div className="alert alert-error">{fehler}</div>}

      <div className="card" style={{ marginBottom: '1rem', padding: '0.75rem 1rem' }}>
        <ZeitraumWahl wert={zeitraum} onChange={setZeitraum} />
      </div>

      {laden ? <div style={{ padding: '2rem', textAlign: 'center' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div> : (
        <>
          {gruppen.length === 0 && (
            <div className="card"><div className="empty-state">
              <div className="empty-state-icon">🏅</div>
              <div className="empty-state-text">Noch keine Leistungsgruppe angelegt</div>
              <button className="btn btn-primary btn-sm" style={{ marginTop: '0.75rem' }} onClick={() => setDialog('neu')}>
                + Leistungsgruppe anlegen
              </button>
            </div></div>
          )}

          {gruppen.map(g => {
            const z = zahl(g.id);
            const istOffen = offen?.id === g.id;
            return (
              <div className="card" key={g.id} style={{ marginBottom: '1rem', opacity: g.aktiv ? 1 : 0.6 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '1rem', flexWrap: 'wrap' }}>
                  <div>
                    <h3 style={{ fontSize: '1.05rem', fontWeight: 700 }}>
                      {g.name}{!g.aktiv && <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}> (inaktiv)</span>}
                    </h3>
                    <div style={{ fontSize: '0.85rem', color: 'var(--text-muted)', marginTop: 2 }}>
                      {g.trainer_name ? `👤 ${g.trainer_name}` : '👤 kein Trainer hinterlegt'}
                      {g.ort ? ` · 📍 ${g.ort}` : ''}
                    </div>
                    <div style={{ fontSize: '0.85rem', marginTop: 4 }}>
                      {(g.termine || []).length > 0
                        ? <span style={{ color: '#1565c0' }}>📅 {g.termine.map(t => `${WOCHENTAGE[t.wochentag]} ${t.uhrzeit_von?.slice(0, 5)}–${t.uhrzeit_bis?.slice(0, 5)}`).join(' · ')} Uhr</span>
                        : <span style={{ color: '#c62828' }}>⚠ Keine Trainingszeit hinterlegt – unten eintragen, sonst lassen sich keine Stunden buchen</span>}
                    </div>
                    <div style={{ fontSize: '0.85rem', marginTop: 2 }}>
                      {(g.bahnen || []).length > 0
                        ? <span style={{ color: 'var(--text-muted)' }}>🏊 {g.bahnen.map(b => `${b.bahn} ${WOCHENTAGE[b.wochentag].slice(0, 2)} ${b.von.slice(0, 5)}–${b.bis.slice(0, 5)}`).join(' · ')}</span>
                        : <span style={{ color: '#e65100' }}>⚠ Keine Bahn zugeordnet – diese Stunden fehlen in der Schwimmbad-Rechnung</span>}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setDialog(g)}>Bearbeiten</button>
                    <button className="btn btn-primary btn-sm" onClick={() => setTrainingDialog({ gruppe: g, vorhanden: null })}>
                      + Training erfassen
                    </button>
                  </div>
                </div>

                <div className="stat-grid" style={{ marginTop: '0.875rem' }}>
                  <div className="stat-card"><div className="stat-label">Trainings</div><div className="stat-value">{z.trainings}</div></div>
                  <div className="stat-card"><div className="stat-label">Schwimmer im Schnitt</div>
                    <div className="stat-value">{z.schnitt_teilnehmer === null ? '–' : fmtZahl(z.schnitt_teilnehmer)}</div></div>
                  <div className="stat-card"><div className="stat-label">Trainerstunden</div><div className="stat-value">{fmtZahl(z.stunden)}</div></div>
                  <div className="stat-card"><div className="stat-label">Betreuende</div><div className="stat-value">{z.personen}</div></div>
                </div>

                <Trainingszeiten gruppe={g} onGeaendert={load} />

                <button className="btn btn-secondary btn-sm" style={{ marginTop: '0.75rem' }}
                  onClick={() => setOffen(istOffen ? null : g)}>
                  {istOffen ? '▾ Trainings ausblenden' : '▸ Trainings anzeigen'}
                </button>

                {istOffen && (
                  trainings === null ? <div style={{ padding: '1rem' }}><div className="loading-spinner" style={{ margin: '0 auto' }} /></div> : (
                    <div className="table-wrap" style={{ marginTop: '0.75rem' }}>
                      <table>
                        <thead><tr><th>Datum</th><th>Schwimmer</th><th>Betreut von</th><th>Stunden</th><th></th></tr></thead>
                        <tbody>
                          {trainings.trainings.length === 0 && (
                            <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '1.5rem' }}>
                              Im gewählten Zeitraum wurde noch kein Training erfasst
                            </td></tr>
                          )}
                          {trainings.trainings.map(t => (
                            <tr key={t.id}>
                              <td>{fmtDatum(t.datum)}</td>
                              <td>{t.teilnehmer_anzahl ?? <span style={{ color: '#e65100' }}>keine Zahl</span>}</td>
                              <td style={{ fontSize: '0.85rem' }}>{t.team.map(x => x.name).join(', ') || '–'}</td>
                              <td>{fmtZahl(t.team.reduce((s, x) => s + x.stunden, 0))}</td>
                              <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                                <button className="btn btn-secondary btn-sm" style={{ padding: '1px 8px' }}
                                  onClick={() => setTrainingDialog({ gruppe: g, vorhanden: t })}>Ändern</button>
                                {!trainings.gesperrt && (
                                  <button className="btn btn-danger btn-sm" style={{ marginLeft: 6, padding: '1px 8px' }}
                                    onClick={() => loeschen(t)}>✕</button>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )
                )}
              </div>
            );
          })}
        </>
      )}

      {dialog && (
        <GruppeDialog gruppe={dialog === 'neu' ? null : dialog} trainer={trainer}
          onClose={() => setDialog(null)}
          onGespeichert={() => { setDialog(null); hinweis.erfolg('Gespeichert'); load(); }} />
      )}
      {trainingDialog && (
        <TrainingDialog gruppe={trainingDialog.gruppe} vorhanden={trainingDialog.vorhanden} team={team}
          onClose={() => setTrainingDialog(null)}
          onGespeichert={() => { setTrainingDialog(null); load(); if (offen) ladeTrainings(offen.id); }} />
      )}
    </div>
  );
}
